import axios from 'axios'
import React, { Component, ErrorInfo } from 'react'
import { UnexpectedErrorSCN, type NavigatorFC } from './UnexpectedErrorSCN'

// Session flag to avoid an infinite reload loop if the chunk keeps failing.
const CHUNK_RELOAD_FLAG = 'kach_chunk_reload'

// A ChunkLoadError almost always means a new deploy replaced the hashed chunk
// files the currently-loaded page references. Reloading once pulls the fresh
// build and resolves it without surfacing a fake "server error" to the user.
const isChunkLoadError = (error: Error): boolean =>
  error?.name === 'ChunkLoadError' ||
  /Loading( CSS)? chunk .* failed/i.test(error?.message ?? '')

export class ErrorBoundarySCN extends Component<
  { children: React.ReactNode; Navigator: NavigatorFC },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; Navigator: NavigatorFC }) {
    super(props)

    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true }
  }

  componentDidMount() {
    // If the app stays alive for a bit after a chunk-triggered reload, the new
    // build loaded fine — clear the guard so a future deploy can reload again.
    // An immediate re-failure won't reach this, preventing reload loops.
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
      }, 10_000)
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      // Reload at most once per session to recover from a stale build cache.
      if (
        typeof window !== 'undefined' &&
        !sessionStorage.getItem(CHUNK_RELOAD_FLAG)
      ) {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
        window.location.reload()
        return
      }
    }

    axios.post('/api/register/error', {
      componentStack: errorInfo?.componentStack,
      error: error.stack,
    })
  }

  render() {
    // Check if the error is thrown
    if (this.state.hasError) {
      return <UnexpectedErrorSCN Navigator={this.props.Navigator} />
    }

    // Return children components in case of no error

    return this.props.children
  }
}
